import { getTheme, IColumn } from "@fluentui/react";

import ProductListItem from "../ItemsStep/ProductListItem";

import { checkCustomBenefits, phoneNumberFormat } from "./helpers";

import { CopyPaste } from "@/components/CopyPaste";
import { Badge } from "@/modules/Contracts/styles";
import { toLocaleString } from "@/utils/ToLocaleString";

const theme = getTheme();

type OnColumnClick = (
  event: React.MouseEvent<HTMLElement>,
  column: IColumn
) => void;

export function createCommonColumns(
  _onColumnClick?: OnColumnClick,
  isSimExempt = false
): Array<IColumn> {
  return [
    {
      key: "line",
      name: "Linha",
      minWidth: 150,
      maxWidth: 200,
      fieldName: "line",
      isRowHeader: true,
      isMultiline: true,
      data: "string",
      isPadded: true,
      onRender: item => {
        const chipInventory =
          item?.inventory?.find(
            inventory => inventory?.product?.category?.code === "CHIP"
          ) ?? null;

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            {checkCustomBenefits(item) && (
              <div>
                <Badge bgColor={theme.semanticColors.severeWarningIcon}>
                  Pacote benefícios personalizada
                </Badge>
              </div>
            )}

            <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>
              <CopyPaste
                valueToCopy={item.line.number}
                tooltipText="Copiar número da linha"
              >
                {phoneNumberFormat(String(item.line.number))}
              </CopyPaste>
            </div>
            <div style={{ marginTop: "5px" }}>
              {item.operation.name} - {item.offerPrice.offer.plan.carrier.name}
            </div>

            {item?.additionalInfo?.iccid && (
              <div
                style={{
                  fontWeight: 600,
                  marginTop: "5px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <strong>ICCID ORIGINAL:</strong>
                <span className="ms-fontWeight-regular">
                  {item?.additionalInfo?.iccid}
                </span>
              </div>
            )}
            {!item?.additionalInfo?.iccid && Boolean(chipInventory) && (
              <div
                style={{
                  fontWeight: 600,
                  marginTop: "5px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center"
                }}
              >
                <strong>ICCID:</strong>
                <span className="ms-fontWeight-regular">
                  {chipInventory?.serialNumber && chipInventory?.serialNumber}
                </span>

                <strong>Centro de Distribuição: </strong>
                <span className="ms-fontWeight-regular">
                  {item.inventory[0]?.distributionCenter?.name}
                </span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: "description",
      name: "Descrição",
      minWidth: 400,
      fieldName: "description",
      isRowHeader: true,
      data: "string",
      isPadded: true,
      isMultiline: true,
      onRender: item => (
        <ProductListItem item={item} isSimExempt={isSimExempt} />
      )
    }
  ];
}

export function createCompactCommonColumns(
  onColumnClick: OnColumnClick
): Array<IColumn> {
  return [
    {
      key: "line",
      name: "Linha",
      fieldName: "line",
      minWidth: 100,
      maxWidth: 120,
      isRowHeader: true,
      data: "string",
      isPadded: true,
      onRender: ({ line: { number } }) => phoneNumberFormat(String(number))
    },
    {
      key: "operation",
      name: "Operação",
      fieldName: "operation.name",
      isSorted: true,
      isSortedDescending: false,
      onColumnClick,
      minWidth: 100,
      maxWidth: 100,
      isPadded: true,
      isMultiline: true,
      onRender: ({ operation }) => operation?.name
    },
    {
      key: "carrier",
      name: "Operadora",
      fieldName: "offerPrice.offer.plan.carrier",
      isSorted: true,
      onColumnClick,
      minWidth: 100,
      maxWidth: 100,
      isPadded: true,
      isMultiline: true,
      onRender: ({
        offerPrice: {
          offer: {
            plan: { carrier }
          }
        }
      }) => carrier.name
    },
    {
      key: "plan",
      name: "Plano de benefícios",
      fieldName: "offerPrice.offer.plan.name",
      isSorted: true,
      onColumnClick,
      minWidth: 100,
      isPadded: true,
      isMultiline: true,
      onRender: ({
        offerPrice: {
          offer: { plan }
        }
      }) => plan.name
    },
    {
      key: "planPrice",
      name: "Valor do plano de benefícios",
      fieldName: "offerOverride.price|offerPrice.price",
      isSorted: true,
      onColumnClick,
      minWidth: 100,
      maxWidth: 100,
      isPadded: true,
      isMultiline: true,
      onRender: ({ offerPrice: { price }, offerOverride }) => {
        const monthlyAdditionalValueSum = 100;

        if (offerOverride?.price) {
          return (
            toLocaleString(offerOverride.price) + monthlyAdditionalValueSum
          );
        }

        return toLocaleString(price);
      }
    },
    {
      key: "discount",
      name: "Descontos",
      fieldName: "offerOverride.discount|offerPrice.offer.discount.value",
      isSorted: true,
      onColumnClick,
      minWidth: 100,
      maxWidth: 100,
      isPadded: true,
      isMultiline: true,
      onRender: ({
        offerPrice: {
          offer: { discount }
        },
        offerOverride
      }) => {
        if (offerOverride?.discount)
          return toLocaleString(offerOverride.discount);

        if (discount?.value) return toLocaleString(discount.value);
      }
    },
    {
      key: "device",
      name: "Aparelho",
      fieldName: "inventory.product?.name",
      isSorted: true,
      onColumnClick,
      minWidth: 100,
      maxWidth: 100,
      isPadded: true,
      isMultiline: true,
      onRender: ({ inventory }) => inventory?.product?.name ?? "-"
    },
    {
      key: "priceValue",
      name: "Valor do aparelho",
      fieldName: "offerPrice.salesConditions.cashValue",
      isSorted: true,
      onColumnClick,
      minWidth: 100,
      isPadded: true,
      isMultiline: true,
      onRender: ({ saleCondition, offerOverride }) => {
        if (offerOverride?.cashValue)
          return toLocaleString(offerOverride.cashValue);

        if (saleCondition?.cashValue)
          return toLocaleString(saleCondition?.cashValue);

        return "-";
      }
    }
  ];
}
